import { useEffect, useState, useRef } from "react";
import axios, { API } from "../../api";

/** Compress a File to a base64 JPEG (max 600x600 long edge). */
async function fileToCompressedBase64(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const maxEdge = 600;
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function timeAgo(iso) {
  if (!iso) return "never mentioned";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

/**
 * Parse a .vcf (vCard) file into [{name, phone, birthday}] entries.
 * Lightweight inline parser — handles vCard 2.1 / 3.0 / 4.0 well enough for
 * the iOS "Export Contacts" use case. No external deps.
 */
function parseVCard(text) {
  const cards = [];
  const blocks = text.split(/BEGIN:VCARD/i).slice(1);
  for (const raw of blocks) {
    const body = raw.split(/END:VCARD/i)[0] || "";
    const lines = body.split(/\r?\n/);
    const entry = { name: "", phone: "", birthday: "" };
    for (let line of lines) {
      // unfold soft-wrapped lines
      line = line.trim();
      if (!line) continue;
      const upper = line.toUpperCase();
      if (upper.startsWith("FN:")) {
        entry.name = line.slice(3).trim();
      } else if (!entry.name && upper.startsWith("N:")) {
        // N:Last;First;Middle;Prefix;Suffix
        const parts = line.slice(2).split(";");
        entry.name = [parts[3], parts[1], parts[2], parts[0], parts[4]]
          .filter(Boolean).join(" ").trim();
      } else if (upper.startsWith("TEL") && line.includes(":")) {
        if (!entry.phone) entry.phone = line.split(":").slice(1).join(":").trim();
      } else if (upper.startsWith("BDAY") && line.includes(":")) {
        const v = line.split(":").slice(1).join(":").trim();
        // YYYYMMDD → YYYY-MM-DD, otherwise pass through
        if (/^\d{8}$/.test(v)) entry.birthday = `${v.slice(0,4)}-${v.slice(4,6)}-${v.slice(6,8)}`;
        else entry.birthday = v.slice(0, 10);
      }
    }
    if (entry.name) cards.push(entry);
  }
  return cards;
}

const QUALITY_OPTIONS = [
  { v: "thriving", label: "💚 Thriving" },
  { v: "good", label: "🙂 Good" },
  { v: "complicated", label: "🤔 Complicated" },
  { v: "rocky", label: "💔 Rocky" },
  { v: "estranged", label: "🪦 Estranged" },
];

export function PeopleModal({ onClose, focusPersonId }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | person object
  const [saving, setSaving] = useState(false);
  const [showDepth, setShowDepth] = useState(false);

  // Basic form state
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [notes, setNotes] = useState("");
  const [photoBase64, setPhotoBase64] = useState("");
  const [birthday, setBirthday] = useState("");
  // Phase 20 — psychology file fields
  const [phone, setPhone] = useState("");
  const [quality, setQuality] = useState("");
  const [howWeMet, setHowWeMet] = useState("");
  const [yearsKnown, setYearsKnown] = useState("");
  const [sharedInterests, setSharedInterests] = useState("");
  const [keyTraits, setKeyTraits] = useState("");
  const [sensitiveTopics, setSensitiveTopics] = useState("");
  // Phase 23 — nicknames + emergency contact + Aura's auto-learned facts
  const [nicknames, setNicknames] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);
  const [emergencyEmail, setEmergencyEmail] = useState("");
  const [learnedFacts, setLearnedFacts] = useState([]);

  // vCard import state
  const [vcfPreview, setVcfPreview] = useState(null); // [{name, phone, birthday, _picked}]
  const [vcfImporting, setVcfImporting] = useState(false);

  const fileInputRef = useRef(null);
  const vcfInputRef = useRef(null);

  useEffect(() => { loadPeople(); }, []);

  useEffect(() => {
    // Auto-open a person's profile if launched with focusPersonId
    if (focusPersonId && people.length > 0) {
      const p = people.find((x) => x.id === focusPersonId);
      if (p) openEdit(p);
    }
  }, [focusPersonId, people]);

  const loadPeople = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/people`);
      setPeople(res.data.people || []);
    } catch (e) {
      console.error("people load", e);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName(""); setRelationship(""); setNotes(""); setPhotoBase64(""); setBirthday("");
    setPhone(""); setQuality(""); setHowWeMet(""); setYearsKnown("");
    setSharedInterests(""); setKeyTraits(""); setSensitiveTopics("");
    setNicknames(""); setIsEmergency(false); setEmergencyEmail("");
    setLearnedFacts([]);
    setShowDepth(false);
  };

  const openNew = () => { setEditing("new"); resetForm(); };

  const openEdit = (p) => {
    setEditing(p);
    setName(p.name || "");
    setRelationship(p.relationship || "");
    setNotes(p.notes || "");
    setPhotoBase64(p.photo_base64 ? `data:image/jpeg;base64,${p.photo_base64}` : "");
    setBirthday(p.birthday || "");
    setPhone(p.phone || "");
    setQuality(p.relationship_quality || "");
    setHowWeMet(p.how_we_met || "");
    setYearsKnown(p.years_known !== null && p.years_known !== undefined ? String(p.years_known) : "");
    setSharedInterests((p.shared_interests || []).join(", "));
    setKeyTraits(p.key_traits || "");
    setSensitiveTopics(p.sensitive_topics || "");
    setNicknames((p.nicknames || []).join(", "));
    setIsEmergency(Boolean(p.is_emergency_contact));
    setEmergencyEmail(p.emergency_email || "");
    setLearnedFacts(p.learned_facts || []);
    // Auto-expand depth section if any deep fields are filled
    setShowDepth(Boolean(
      p.phone || p.relationship_quality || p.how_we_met ||
      p.years_known || (p.shared_interests || []).length || p.key_traits || p.sensitive_topics ||
      (p.nicknames || []).length || p.is_emergency_contact
    ));
  };

  // Phase 23 — confirm / dismiss an auto-learned fact. Optimistic UI.
  const confirmFact = async (factId) => {
    setLearnedFacts((prev) => prev.map((f) => f.id === factId ? { ...f, confirmed_by_user: true } : f));
    try {
      await axios.post(`${API}/people/${editing.id}/learned-fact/${factId}/confirm`);
    } catch (e) {
      console.error("confirm fact", e);
    }
  };
  const dismissFact = async (factId) => {
    setLearnedFacts((prev) => prev.filter((f) => f.id !== factId));
    try {
      await axios.delete(`${API}/people/${editing.id}/learned-fact/${factId}`);
      await loadPeople();
    } catch (e) {
      console.error("dismiss fact", e);
    }
  };

  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToCompressedBase64(file);
      setPhotoBase64(dataUrl);
    } catch (err) {
      console.error("photo compress error", err);
    }
  };

  const importContactAndroid = async () => {
    if (!("contacts" in navigator) || !("ContactsManager" in window)) {
      alert("Contact picker is only available on Android Chrome. Use the .vcf import for iOS.");
      return;
    }
    try {
      const props = ["name", "tel"];
      const opts = { multiple: false };
      const contacts = await navigator.contacts.select(props, opts);
      if (contacts && contacts.length > 0) {
        const c = contacts[0];
        if (c.name && c.name.length > 0) setName(c.name[0]);
        if (c.tel && c.tel.length > 0) setPhone(c.tel[0]);
      }
    } catch (e) {
      console.error("contact pick error", e);
    }
  };

  // --- iOS .vcf import (works everywhere — main path for iOS users) ---
  const handleVcfPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const cards = parseVCard(text);
      if (cards.length === 0) {
        alert("No contacts found in that file. Make sure you exported a .vcf from your Contacts app.");
        return;
      }
      setVcfPreview(cards.map((c) => ({ ...c, _picked: true })));
    } catch (err) {
      console.error("vcf parse error", err);
      alert("Couldn't read that file. Make sure it's a .vcf export.");
    } finally {
      e.target.value = "";
    }
  };

  const importSelectedVcfContacts = async () => {
    if (!vcfPreview) return;
    const toImport = vcfPreview.filter((c) => c._picked);
    if (toImport.length === 0) { setVcfPreview(null); return; }
    setVcfImporting(true);
    try {
      // import sequentially to keep the backend gentle
      for (const c of toImport) {
        await axios.post(`${API}/people`, {
          name: c.name,
          phone: c.phone || null,
          birthday: c.birthday || null,
        });
      }
      await loadPeople();
      setVcfPreview(null);
    } catch (e) {
      console.error("vcf import error", e);
      alert(e.response?.data?.detail || "Some contacts couldn't be imported.");
    } finally {
      setVcfImporting(false);
    }
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        relationship: relationship.trim() || null,
        notes: notes.trim() || null,
        photo_base64: photoBase64 ? photoBase64.split(",")[1] || photoBase64 : null,
        birthday: birthday || null,
        phone: phone.trim() || null,
        relationship_quality: quality || null,
        how_we_met: howWeMet.trim() || null,
        years_known: yearsKnown ? parseInt(yearsKnown, 10) : null,
        shared_interests: sharedInterests
          ? sharedInterests.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
        key_traits: keyTraits.trim() || null,
        sensitive_topics: sensitiveTopics.trim() || null,
        nicknames: nicknames
          ? nicknames.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        is_emergency_contact: isEmergency,
        emergency_email: emergencyEmail.trim() || null,
      };
      if (editing === "new") {
        await axios.post(`${API}/people`, payload);
      } else {
        await axios.patch(`${API}/people/${editing.id}`, payload);
      }
      await loadPeople();
      setEditing(null);
    } catch (e) {
      console.error("save person", e);
      alert(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing || editing === "new") return;
    if (!confirm(`Remove ${editing.name}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/people/${editing.id}`);
      await loadPeople();
      setEditing(null);
    } catch (e) {
      console.error("delete person", e);
    } finally {
      setSaving(false);
    }
  };

  const showContactPicker = typeof window !== "undefined" && "contacts" in (navigator || {});

  // ============== vCard preview view ==============
  if (vcfPreview) {
    const pickedCount = vcfPreview.filter((c) => c._picked).length;
    return (
      <div className="modal-overlay" onClick={() => !vcfImporting && setVcfPreview(null)}>
        <div className="people-modal" onClick={(e) => e.stopPropagation()} data-testid="vcf-preview-modal">
          <div className="journal-header">
            <h2>📥 Import Contacts</h2>
            <button className="journal-close" onClick={() => !vcfImporting && setVcfPreview(null)} data-testid="vcf-cancel-btn">×</button>
          </div>
          <p className="journal-subtitle">
            Found {vcfPreview.length} contact{vcfPreview.length === 1 ? "" : "s"}.
            Pick the ones you want Aura to know — you can add more details after.
          </p>
          <div className="vcf-bulk-actions">
            <button
              className="ghost-btn small"
              onClick={() => setVcfPreview((prev) => prev.map((c) => ({ ...c, _picked: true })))}
              data-testid="vcf-select-all-btn"
            >
              Select all
            </button>
            <button
              className="ghost-btn small"
              onClick={() => setVcfPreview((prev) => prev.map((c) => ({ ...c, _picked: false })))}
              data-testid="vcf-select-none-btn"
            >
              Clear
            </button>
          </div>
          <div className="people-list">
            {vcfPreview.map((c, i) => (
              <label
                key={i}
                className={`person-row vcf-row ${c._picked ? "vcf-row-picked" : ""}`}
                data-testid={`vcf-row-${i}`}
              >
                <input
                  type="checkbox"
                  checked={c._picked}
                  onChange={() => setVcfPreview((prev) => prev.map((x, j) => j === i ? { ...x, _picked: !x._picked } : x))}
                  data-testid={`vcf-check-${i}`}
                />
                <div className="person-info">
                  <strong>{c.name}</strong>
                  <span>{c.phone || "no phone"}{c.birthday ? ` · 🎂 ${c.birthday}` : ""}</span>
                </div>
              </label>
            ))}
          </div>
          <div className="people-form-actions">
            <button
              className="init-btn"
              onClick={importSelectedVcfContacts}
              disabled={vcfImporting || pickedCount === 0}
              data-testid="vcf-import-btn"
            >
              {vcfImporting ? "Importing…" : `Add ${pickedCount} to Aura's circle`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============== Edit / New form ==============
  if (editing) {
    const isExisting = editing !== "new";
    return (
      <div className="modal-overlay" onClick={() => !saving && setEditing(null)}>
        <div className="people-form-modal" onClick={(e) => e.stopPropagation()}>
          <div className="journal-header">
            <h2>{editing === "new" ? "Add Someone" : "Edit"}</h2>
            <button className="journal-close" onClick={() => !saving && setEditing(null)} data-testid="people-form-close">×</button>
          </div>

          {isExisting && (
            <div className="profile-completeness" data-testid="profile-completeness">
              <div className="profile-completeness-bar">
                <div className="profile-completeness-fill" style={{ width: `${editing.profile_completeness || 0}%` }} />
              </div>
              <span>{editing.profile_completeness || 0}% — Aura knows {(editing.profile_completeness || 0) < 30 ? "the basics" : (editing.profile_completeness || 0) < 70 ? "a fair bit" : "them well"}</span>
            </div>
          )}

          <div className="people-form-body">
            <div className="people-photo-picker" onClick={() => fileInputRef.current?.click()} data-testid="people-photo-picker">
              {photoBase64 ? (
                <img src={photoBase64} alt="" className="people-photo-preview" />
              ) : (
                <div className="people-photo-placeholder">+ Photo</div>
              )}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handlePhotoPick}
                style={{ display: "none" }}
                data-testid="people-photo-input"
              />
            </div>

            <input type="text" className="init-input" placeholder="Name *" value={name} onChange={(e) => setName(e.target.value)} data-testid="people-name-input" />
            <input type="text" className="init-input" placeholder="Relationship (sister, best friend, etc.)" value={relationship} onChange={(e) => setRelationship(e.target.value)} data-testid="people-relationship-input" />
            <textarea className="init-input people-notes" placeholder="Quick notes for Aura (stressed about exams, loves cooking, etc.)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="people-notes-input" />
            <input type="date" className="init-input" placeholder="Birthday" value={birthday} onChange={(e) => setBirthday(e.target.value)} data-testid="people-birthday-input" />

            {/* Collapsible psychology file */}
            <button
              type="button"
              className="ghost-btn depth-toggle"
              onClick={() => setShowDepth((v) => !v)}
              data-testid="people-depth-toggle"
            >
              {showDepth ? "▾" : "▸"} Tell Aura more about them
            </button>

            {showDepth && (
              <div className="people-depth-section" data-testid="people-depth-section">
                <input type="tel" className="init-input" placeholder="Phone (for future voice features)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="people-phone-input" />

                <input type="text" className="init-input" placeholder="Nicknames Aura might hear (Mama, Pops, M)" value={nicknames} onChange={(e) => setNicknames(e.target.value)} data-testid="people-nicknames-input" />

                <label className="depth-label">How are things between you?</label>
                <div className="quality-picker">
                  {QUALITY_OPTIONS.map((q) => (
                    <button
                      key={q.v}
                      type="button"
                      className={`quality-chip ${quality === q.v ? "active" : ""}`}
                      onClick={() => setQuality(quality === q.v ? "" : q.v)}
                      data-testid={`quality-${q.v}`}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>

                <input type="text" className="init-input" placeholder="How did you meet?" value={howWeMet} onChange={(e) => setHowWeMet(e.target.value)} data-testid="people-how-met-input" />
                <input type="number" min="0" max="80" className="init-input" placeholder="Years known" value={yearsKnown} onChange={(e) => setYearsKnown(e.target.value)} data-testid="people-years-input" />
                <input type="text" className="init-input" placeholder="Shared interests (climbing, anime, vintage records)" value={sharedInterests} onChange={(e) => setSharedInterests(e.target.value)} data-testid="people-interests-input" />
                <textarea className="init-input people-notes" placeholder="Their vibe — what should Aura feel about them?" value={keyTraits} onChange={(e) => setKeyTraits(e.target.value)} rows={2} data-testid="people-traits-input" />
                <textarea className="init-input people-notes people-notes-warn" placeholder="⚠️ Anything Aura should NEVER bring up? (sensitive topics, recent loss, exes)" value={sensitiveTopics} onChange={(e) => setSensitiveTopics(e.target.value)} rows={2} data-testid="people-sensitive-input" />

                <label className="depth-label" style={{ marginTop: "0.6rem" }}>
                  <input
                    type="checkbox"
                    checked={isEmergency}
                    onChange={(e) => setIsEmergency(e.target.checked)}
                    data-testid="people-emergency-toggle"
                    style={{ marginRight: "0.5rem", width: "16px", height: "16px", verticalAlign: "middle" }}
                  />
                  🛟 Emergency contact — Aura emails them if she detects a crisis signal
                </label>
                {isEmergency && (
                  <input
                    type="email"
                    className="init-input"
                    placeholder="Their email (where the heads-up goes)"
                    value={emergencyEmail}
                    onChange={(e) => setEmergencyEmail(e.target.value)}
                    data-testid="people-emergency-email-input"
                  />
                )}
              </div>
            )}

            {/* Phase 23 — Auto-learned facts. User can confirm or dismiss anything Aura got wrong. */}
            {editing !== "new" && learnedFacts.length > 0 && (
              <div className="learned-facts-section" data-testid="learned-facts-section">
                <div className="learned-facts-header">
                  ✨ Aura learned about {editing.name?.split(" ")[0] || "them"}
                </div>
                {learnedFacts.slice().reverse().map((f) => (
                  <div key={f.id} className="learned-fact-row" data-testid={`learned-fact-${f.id}`}>
                    <div className="learned-fact-text">
                      <span className="learned-fact-field">{f.field?.replace("_", " ")}:</span>
                      <span className="learned-fact-value">{f.value}</span>
                      {f.source_excerpt && (
                        <span className="learned-fact-source">"…{f.source_excerpt}…"</span>
                      )}
                    </div>
                    {f.confirmed_by_user ? (
                      <span className="learned-fact-confirmed-pill">✓ confirmed</span>
                    ) : (
                      <div className="learned-fact-actions">
                        <button
                          type="button"
                          className="learned-fact-btn confirm"
                          onClick={() => confirmFact(f.id)}
                          data-testid={`learned-fact-confirm-${f.id}`}
                          title="Yes, that's right"
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          className="learned-fact-btn dismiss"
                          onClick={() => dismissFact(f.id)}
                          data-testid={`learned-fact-dismiss-${f.id}`}
                          title="Remove this — Aura was wrong"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showContactPicker && editing === "new" && (
              <button className="ghost-btn small" onClick={importContactAndroid} data-testid="people-import-contact-btn">
                📱 Import from Contacts (Android)
              </button>
            )}

            <div className="people-form-actions">
              {editing !== "new" && (
                <button className="ghost-btn delete-btn" onClick={remove} disabled={saving} data-testid="people-delete-btn">
                  Remove
                </button>
              )}
              <button className="init-btn" onClick={save} disabled={saving || !name.trim()} data-testid="people-save-btn">
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============== List view ==============
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="people-modal" onClick={(e) => e.stopPropagation()} data-testid="people-modal">
        <div className="journal-header">
          <h2>👥 People Aura Remembers</h2>
          <button className="journal-close" onClick={onClose} data-testid="people-close-btn">×</button>
        </div>
        <p className="journal-subtitle">Aura uses this to talk to you like a friend who knows your circle.</p>

        <div className="people-action-row">
          <button className="init-btn" onClick={openNew} data-testid="people-add-btn">
            + Add Someone
          </button>
          <button
            className="ghost-btn small"
            onClick={() => vcfInputRef.current?.click()}
            data-testid="people-import-vcf-btn"
          >
            📥 Import from file
          </button>
          <input
            type="file"
            accept=".vcf,text/vcard,text/x-vcard"
            ref={vcfInputRef}
            onChange={handleVcfPick}
            style={{ display: "none" }}
            data-testid="people-vcf-input"
          />
        </div>
        <p className="people-import-hint">
          📱 iOS: Contacts app → tap a contact → Share → Save to Files → upload the .vcf here.
        </p>

        {loading ? (
          <div className="journal-loading">Loading...</div>
        ) : people.length === 0 ? (
          <div className="journal-empty">
            <p>👥</p>
            <p>No one yet. Add family, friends, or anyone Aura should know about.</p>
          </div>
        ) : (
          <div className="people-list">
            {people.map((p) => (
              <div
                key={p.id}
                className="person-row"
                onClick={() => openEdit(p)}
                data-testid={`person-${p.id}`}
              >
                <div className="person-avatar">
                  {p.photo_base64 ? (
                    <img src={`data:image/jpeg;base64,${p.photo_base64}`} alt="" />
                  ) : (
                    <div className="person-avatar-placeholder">{p.name?.[0]?.toUpperCase() || "?"}</div>
                  )}
                </div>
                <div className="person-info">
                  <strong>{p.name}{p.is_emergency_contact && <span className="person-emergency-badge" data-testid={`emergency-badge-${p.id}`}>🛟 Emergency</span>}</strong>
                  <span>{p.relationship || "—"} · last mentioned {timeAgo(p.last_mentioned)}</span>
                  {p.notes && <em>{p.notes}</em>}
                </div>
                <span className="person-mentions">{p.times_mentioned || 0}×</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
